import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Link} from 'react-router-dom';
import {getOrder} from '../api';
import {getSavedOrderIds, removeOrderFromHistory, saveOrderToHistory} from '../utils/orderHistory';
import './OrdersPage.css';

const formatPrice = (price) => `${Number(price).toLocaleString('ru-RU')} руб.`;

function OrdersPage() {
    const token = localStorage.getItem('token');
    const [orders, setOrders] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [lookupOrderId, setLookupOrderId] = useState('');

    const totalOrdersPrice = useMemo(
        () => orders.reduce((sum, order) => sum + Number(order.total_price || 0), 0),
        [orders]
    );

    const loadOrders = useCallback(async () => {
        setIsLoading(true);
        setMessage('');

        const savedOrderIds = getSavedOrderIds();

        if (savedOrderIds.length === 0) {
            setOrders([]);
            setIsLoading(false);
            return;
        }

        try {
            const orderResults = await Promise.all(
                savedOrderIds.map(async orderId => {
                    const orderData = await getOrder(token, orderId);

                    if (!orderData.order_id) {
                        removeOrderFromHistory(orderId);
                        return null;
                    }

                    return orderData;
                })
            );

            setOrders(orderResults.filter(Boolean));
        } finally {
            setIsLoading(false);
        }
    }, [token]);

    useEffect(() => {
        loadOrders();
    }, [loadOrders]);

    const handleLookupOrder = async (event) => {
        event.preventDefault();
        setMessage('');

        const normalizedOrderId = Number(lookupOrderId);

        if (!Number.isInteger(normalizedOrderId) || normalizedOrderId <= 0) {
            setMessage('Введите номер заказа');
            return;
        }

        const orderData = await getOrder(token, normalizedOrderId);

        if (!orderData.order_id) {
            setMessage(orderData.message || 'Заказ не найден');
            return;
        }

        saveOrderToHistory(orderData.order_id);
        setLookupOrderId('');
        setMessage(`Заказ №${orderData.order_id} добавлен в историю`);
        loadOrders();
    };

    return (
        <section className="page orders-page" data-testid="orders-page">
            <header className="page-header orders-header">
                <div>
                    <p className="page-kicker">История</p>
                    <h1 className="page-title" data-testid="orders-title">Мои заказы</h1>
                </div>
                <div className="orders-count" data-testid="orders-count" aria-label={`${orders.length} заказов`}>
                    <strong data-testid="orders-count-value">{orders.length}</strong>
                    <span>заказов</span>
                </div>
            </header>

            <div className="orders-tools">
                <form className="orders-lookup panel" onSubmit={handleLookupOrder} data-testid="order-lookup-form">
                    <div className="field orders-lookup-field">
                        <label htmlFor="order-lookup-id">Номер заказа</label>
                        <input
                            id="order-lookup-id"
                            type="number"
                            placeholder="101"
                            value={lookupOrderId}
                            onChange={event => setLookupOrderId(event.target.value)}
                            data-testid="order-lookup-input"
                        />
                    </div>
                    <button type="submit" className="btn btn-primary" data-testid="order-lookup-button">
                        Найти
                    </button>
                </form>

                <button
                    type="button"
                    className="btn btn-muted orders-refresh-button"
                    onClick={loadOrders}
                    data-testid="orders-refresh-button"
                >
                    Обновить
                </button>
            </div>

            {message && (
                <div className="notice notice-success orders-message" data-testid="orders-message">
                    {message}
                </div>
            )}

            {isLoading ? (
                <div className="panel empty-state" data-testid="orders-loading">
                    Загрузка заказов...
                </div>
            ) : orders.length === 0 ? (
                <div className="panel empty-state" data-testid="orders-empty">
                    Заказов пока нет
                </div>
            ) : (
                <div className="orders-layout">
                    <div className="orders-list" data-testid="orders-list">
                        {orders.map(order => {
                            const itemCount = order.items.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
                            const previewItems = order.items.slice(0, 3);

                            return (
                                <article
                                    className="order-history-card panel"
                                    key={order.order_id}
                                    data-testid="order-history-card"
                                    data-order-id={order.order_id}
                                >
                                    <div className="order-history-main">
                                        <div>
                                            <p className="order-history-label">Заказ</p>
                                            <h2 className="order-history-title" data-testid="order-history-id">
                                                №{order.order_id}
                                            </h2>
                                        </div>
                                        <span className="order-status" data-testid="order-history-status">
                                            Оформлен
                                        </span>
                                    </div>

                                    <div className="order-history-facts">
                                        <div>
                                            <span>Дата</span>
                                            <strong data-testid="order-history-date">{order.created_at}</strong>
                                        </div>
                                        <div>
                                            <span>Товары</span>
                                            <strong data-testid="order-history-item-count">{itemCount}</strong>
                                        </div>
                                        <div>
                                            <span>Сумма</span>
                                            <strong data-testid="order-history-total">{formatPrice(order.total_price)}</strong>
                                        </div>
                                    </div>

                                    <div className="order-preview" data-testid="order-history-preview">
                                        {previewItems.map(item => (
                                            <span key={`${order.order_id}-${item.item_id}`}>
                                                {item.name} x {item.quantity}
                                            </span>
                                        ))}
                                    </div>

                                    <Link
                                        to={`/order/${order.order_id}`}
                                        className="btn btn-primary order-open-link"
                                        data-testid="order-history-open-link"
                                    >
                                        Открыть заказ
                                    </Link>
                                </article>
                            );
                        })}
                    </div>

                    <aside className="orders-summary panel" data-testid="orders-summary">
                        <h2>Итого</h2>
                        <div className="orders-summary-row">
                            <span>Заказы</span>
                            <strong data-testid="orders-summary-count">{orders.length}</strong>
                        </div>
                        <div className="orders-summary-row">
                            <span>Сумма</span>
                            <strong data-testid="orders-summary-total">{formatPrice(totalOrdersPrice)}</strong>
                        </div>
                    </aside>
                </div>
            )}
        </section>
    );
}

export default OrdersPage;
